import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CollectionIcon } from '@heroicons/react/solid';

import { Table, Modal, Loader, SectionTitle } from '../components';
import { fetchOrders, deleteOrder } from '../redux/actions';
import { formatCurrency, truncate, formatDate } from '../utils';

const rows = [
  { field: '_id', title: 'ID' },
  { field: 'orderId', title: 'Order ID', hidden: true },
  { field: 'name', title: 'Name' },
  { field: 'fullName', title: 'Full Name', hidden: true },
  { field: 'orderItems', title: 'Order Items', hidden: true },
  { field: 'fullAddress', title: 'Address', hidden: true },
  { field: 'email', title: 'Email' },
  { field: 'items', title: 'No of Items' },
  { field: 'total', title: 'Total Amount' },
  { field: 'date', title: 'Order Date' },
  { field: 'address', title: 'Address' },
];

const columns = (arr) => {
  return arr.map((i) => ({
    _id: truncate(i._id, 10),
    orderId: i._id,
    name: `${i.first_name} ${i.last_name}`,
    fullName: `${i.first_name} ${i.last_name}`,
    email: i.email,
    items: i.cartItems.reduce((a, i) => (a += i.qty), 0),
    total: formatCurrency(i.total),
    date: formatDate(i.createdAt),
    address: truncate(i.address, 20),
    fullAddress: i.address,
    orderItems: orderItems(i.cartItems),
  }));
};

const orderItems = (items) => (
  <ul
    role="list"
    className="border border-gray-200 rounded-md divide-y divide-gray-200"
  >
    {items.map((i) => (
      <li
        key={i._id}
        className="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
      >
        <div className="w-0 flex-1 flex items-center">
          <CollectionIcon
            className="flex-shrink-0 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
          <span className="ml-2">{i.name}</span>
        </div>
        <div className="ml-4 flex-shrink-0">
          <div className="font-medium text-sky-600 hover:text-sky-500">
            {`${formatCurrency(i.price)} * ${i.qty}pcs`}
          </div>
        </div>
      </li>
    ))}
  </ul>
);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ManageOrders() {
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const dispatch = useDispatch();
  const { items } = useSelector((state) => state.orders);

  useEffect(() => {
    dispatch(fetchOrders());
  }, []);

  return (
    <section className="mt-6 px-6">
      {items ? (
        <>
          <div className="mb-3">
            <SectionTitle label="Manage Orders" />
          </div>
          <Table
            rows={rows}
            columns={items?.length > 0 ? columns(items) : []}
            actions={[
              {
                name: 'Details',
                handleClick: (product) => {
                  setOpenModal(true);
                  setModalContent(product);
                },
                color: 'sky',
              },
              {
                name: 'Delete',
                handleClick: (order) => {
                  dispatch(deleteOrder(order.orderId));
                },
                color: 'red',
              },
            ]}
          />
          <Modal open={openModal} setOpen={setOpenModal}>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg w-full">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Order Information
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Order details and information.
                </p>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  {[
                    'orderId',
                    'fullName',
                    'email',
                    'date',
                    'fullAddress',
                    'orderItems',
                    'total',
                  ].map((d, ind) => (
                    <div
                      key={d}
                      className={classNames(
                        ind % 2 === 0 ? 'bg-gray-50' : 'bg-white',
                        'px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'
                      )}
                    >
                      <dt className="text-sm font-medium text-gray-500">
                        {rows.find((r) => r.field === d)?.title}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 text-right">
                        {modalContent?.[d]}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </Modal>
        </>
      ) : (
        <Loader />
      )}
    </section>
  );
}
